import PropTypes from 'prop-types';
import { Box, Button, Flex, Link, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Skeleton, Text } from '@chakra-ui/react';
import { FiBriefcase, FiCpu, FiSearch, FiUser } from 'react-icons/fi';
import { hostname } from '@/app/services/nucleus.js';

const KINDS = {
  user: { label: 'User', icon: FiBriefcase, bg: 'purple.50', hoverBg: 'purple.100', color: 'purple.700', tagBg: 'brand.900' },
  candidate: { label: 'Candidate', icon: FiUser, bg: 'blue.50', hoverBg: 'blue.100', color: 'blue.800', tagBg: 'brand.800' },
};

const pillProps = {
  alignItems: 'center',
  borderRadius: 'full',
  display: 'inline-flex',
  fontSize: 'xs',
  gap: 2,
  maxWidth: 'fit-content',
  py: 1,
  whiteSpace: 'nowrap',
};

const DetailField = ({ label, value, isMonospace }) => (
  <Text mt={1} wordBreak="break-word">
    <Box as="span" color="gray.300">
      {label}:{' '}
    </Box>
    {/* An id has no spaces to wrap at, so it has to be allowed to break mid-value to stay readable. */}
    <Box as="span" fontFamily={isMonospace ? 'Fira Code, monospace' : undefined} wordBreak={isMonospace ? 'break-all' : undefined}>
      {value}
    </Box>
  </Text>
);

DetailField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  isMonospace: PropTypes.bool,
};

// The jump redirect resolves the Bullhorn id from the active system id row, so only an active
// one can be linked. An inactive id is still worth showing support, just as text.
const bullhornRecord = (actor) => {
  const entries = actor.system_ids?.filter((entry) => entry.name === 'bullhorn') ?? [];
  const active = entries.find((entry) => entry.is_active);
  return { bullhornId: (active ?? entries[0])?.system_id, isLinkable: Boolean(active) };
};

const ActorBadge = ({ actorKind, actor, isSystem, isLoading, onResolve }) => {
  if (isSystem) {
    return (
      <Flex {...pillProps} px={3} bg="gray.100" color="gray.700" aria-label="system actor">
        <FiCpu aria-hidden="true" />
        <Text>System</Text>
      </Flex>
    );
  }

  const kind = KINDS[actor?.kind ?? actorKind];

  // A caller that resolves without being asked has no kind to show, and so no button to spin.
  if (!kind) {
    return isLoading ? <Skeleton height="26px" width="150px" borderRadius="full" aria-label="loading actor" /> : null;
  }

  const kindTag = (
    <Flex alignItems="center" gap={1} px={2} py={0.5} borderRadius="full" bg={kind.tagBg} color="white" fontWeight="semibold">
      <kind.icon aria-hidden="true" />
      {kind.label}
    </Flex>
  );

  // The row's own ids give away the kind, so only the name and details cost a request.
  if (!actor) {
    return (
      <Button
        {...pillProps}
        aria-label={`look up ${kind.label}`}
        onClick={onResolve}
        isLoading={isLoading}
        pl={1}
        pr={3}
        height="auto"
        minWidth={0}
        fontSize="xs"
        fontWeight="normal"
        bg={kind.bg}
        color={kind.color}
        _hover={{ bg: kind.hoverBg }}
        _active={{ bg: kind.hoverBg }}
      >
        {kindTag}
        <FiSearch aria-hidden="true" />
      </Button>
    );
  }

  const { bullhornId, isLinkable } = bullhornRecord(actor);

  return (
    // `strategy` keeps the bubble clear of the audit table's horizontal scroll clipping, and leaving it
    // unportalled puts it next to the badge in the tab order so the Bullhorn link is reachable by keyboard.
    <Popover trigger="hover" placement="top" strategy="fixed" closeDelay={300} isLazy>
      <PopoverTrigger>
        <Flex {...pillProps} pl={1} pr={3} tabIndex={0} aria-label={`${kind.label} ${actor.name}`} bg={kind.bg} color={kind.color}>
          {kindTag}
          <Text>{actor.name}</Text>
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        minWidth="15rem"
        maxWidth="min(22rem, 90vw)"
        bg="gray.800"
        color="white"
        border="none"
        _focusVisible={{ boxShadow: 'none' }}
      >
        <PopoverArrow bg="gray.800" />
        <PopoverBody fontSize="xs" p={3}>
          <Text fontSize="sm" fontWeight="bold">
            {actor.name}
          </Text>
          <DetailField label="Kind" value={kind.label} />
          <DetailField label="Email" value={actor.email} />
          <DetailField label="Nucleus ID" value={actor.id} isMonospace />
          {bullhornId && <DetailField label={isLinkable ? 'Bullhorn ID' : 'Bullhorn ID (inactive)'} value={bullhornId} isMonospace />}
          {isLinkable && (
            <Link
              href={`${hostname}/redirects/jump/${actor.kind}/${actor.id}/bullhorn`}
              isExternal
              mt={2}
              display="inline-block"
              textDecoration="underline"
            >
              Open in Bullhorn ↗
            </Link>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

ActorBadge.propTypes = {
  actorKind: PropTypes.oneOf(['user', 'candidate']),
  actor: PropTypes.shape({
    kind: PropTypes.oneOf(['user', 'candidate']),
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    system_ids: PropTypes.array,
  }),
  isSystem: PropTypes.bool,
  isLoading: PropTypes.bool,
  onResolve: PropTypes.func,
};

export default ActorBadge;
